import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { debounce } from 'lodash';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  FolderOpen as FolderIcon,
  Cloud as CloudIcon,
  DeleteOutline as DeleteIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  LinkOff as DetachIcon,
  Link as AttachIcon,
  Policy as PolicyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

const accentColor = '#4698D3';

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const dialogPaperProps = {
  sx: { borderRadius: '12px', border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF' },
};

// Internal policies that should not be displayed to customers.
const HIDDEN_POLICY_NAMES = [
  'Deny-Payments-CloseAccount-TM',
  'Deny-Redeem-Credits-TM-L2',
  'Deny-Leave-Org-TM-L2',
  'Deny-Cloudfront-Savings-Bundle-TM-L1',
  'Deny-All-RI-TM-L3',
  'Deny-Root-Login-TM-L2',
];

const POLICY_TYPES = [
  {
    key: 'SERVICE_CONTROL_POLICY',
    label: 'Service Control Policies',
    icon: SecurityIcon,
    description: 'Restrict permissions across accounts',
    hiddenNames: HIDDEN_POLICY_NAMES,
    hasValidation: true,
    validationPolicyType: 'SERVICE_CONTROL_POLICY',
    hasFullAWSAccessWarning: true,
    defaultContent: JSON.stringify({ Version: '2012-10-17', Statement: [{ Effect: 'Deny', Action: '*', Resource: '*' }] }, null, 2),
  },
  {
    key: 'RESOURCE_CONTROL_POLICY',
    label: 'Resource Control Policies',
    icon: SecurityIcon,
    description: 'Centrally control resource access',
    hiddenNames: [],
    hasValidation: true,
    validationPolicyType: 'RESOURCE_CONTROL_POLICY',
    hasFullAWSAccessWarning: false,
    defaultContent: JSON.stringify({ Version: '2012-10-17', Statement: [{ Effect: 'Deny', Action: '*', Resource: '*', Principal: '*' }] }, null, 2),
  },
  {
    key: 'TAG_POLICY',
    label: 'Tag Policies',
    icon: PolicyIcon,
    description: 'Standardize tags across resources',
    hiddenNames: [],
    hasValidation: false,
    hasFullAWSAccessWarning: false,
    defaultContent: JSON.stringify({ tags: { CostCenter: { tag_key: { '@@assign': 'CostCenter' }, tag_value: { '@@assign': ['100', '200'] } } } }, null, 2),
  },
  {
    key: 'BACKUP_POLICY',
    label: 'Backup Policies',
    icon: PolicyIcon,
    description: 'Manage backup plans across accounts',
    hiddenNames: [],
    hasValidation: false,
    hasFullAWSAccessWarning: false,
    defaultContent: JSON.stringify({ plans: {} }, null, 2),
  },
  {
    key: 'AISERVICES_OPT_OUT_POLICY',
    label: 'AI Services Opt-Out Policies',
    icon: PolicyIcon,
    description: 'Control AI service data usage',
    hiddenNames: [],
    hasValidation: false,
    hasFullAWSAccessWarning: false,
    defaultContent: JSON.stringify({ services: { '@@operators_allowed_for_child_policies': ['@@none'], default: { opt_out_policy: { '@@assign': 'optOut' } } } }, null, 2),
  },
];

// ---------------------------------------------------------------------------
// OrgTreeCheckbox — recursive tree with checkboxes for attach target selection
// ---------------------------------------------------------------------------
const OrgTreeCheckbox = ({ node, depth = 0, selectedIds, onToggle, excludeIds = [], hideRoot = false, quotaMap = null }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const isExcluded = excludeIds.includes(node.id);
  const isSelected = selectedIds.some((n) => n.id === node.id);
  const isHidden = hideRoot && node.type === 'ROOT';
  const quota = quotaMap ? quotaMap[node.id] : undefined;
  const atCapacity = quota !== undefined && quota >= 5;

  const getIcon = () => {
    switch (node.type) {
      case 'ROOT': return <BusinessIcon sx={{ fontSize: '1rem', color: '#4698D3' }} />;
      case 'OU': return <FolderIcon sx={{ fontSize: '1rem', color: '#F39C12' }} />;
      case 'ACCOUNT': return <CloudIcon sx={{ fontSize: '1rem', color: '#27AE60' }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      {!isHidden && (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
          {hasChildren ? (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ mr: 0.5, p: 0.25 }}>
              {expanded ? <ExpandMoreIcon sx={{ fontSize: '1rem' }} /> : <ExpandLessIcon sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />}
            </IconButton>
          ) : (
            <Box sx={{ width: 24, mr: 0.5 }} />
          )}
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={isExcluded || atCapacity}
            onChange={() => onToggle({ id: node.id, name: node.name, type: node.type })}
            sx={{ p: 0.25, mr: 0.5 }}
          />
          {getIcon()}
          <Typography sx={{ ml: 0.5, fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: isExcluded ? '#999' : '#253E66' }}>
            {node.name}
          </Typography>
          {node.type === 'ACCOUNT' && node.id && (
            <Typography sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', color: '#999' }}>
              {node.id}
            </Typography>
          )}
          <Chip
            label={node.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : node.type}
            size="small"
            sx={{ ml: 0.5, fontSize: '0.6rem', height: 18, bgcolor: node.type === 'ACCOUNT' ? 'rgba(39, 174, 96, 0.1)' : node.type === 'OU' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(70, 152, 211, 0.1)', color: node.type === 'ACCOUNT' ? '#27AE60' : node.type === 'OU' ? '#F39C12' : '#4698D3' }}
          />
          {isExcluded && (
            <Chip label="attached" size="small" sx={{ ml: 0.5, fontSize: '0.6rem', height: 18, bgcolor: '#F5F5F5', color: '#999' }} />
          )}
          {quota !== undefined && quota >= 0 && (
            <Chip
              label={`${quota}/5`}
              size="small"
              sx={{
                ml: 0.5, fontSize: '0.6rem', height: 18, fontWeight: 600,
                bgcolor: atCapacity ? 'rgba(231, 76, 60, 0.1)' : 'rgba(70, 152, 211, 0.08)',
                color: atCapacity ? '#E74C3C' : '#666',
              }}
            />
          )}
        </Box>
      )}
      {hasChildren && (isHidden || expanded) && (
        <Collapse in={isHidden || expanded}>
          {node.children.map((child) => (
            <OrgTreeCheckbox
              key={child.id}
              node={child}
              depth={isHidden ? depth : depth + 1}
              selectedIds={selectedIds}
              onToggle={onToggle}
              excludeIds={excludeIds}
              hideRoot={false}
              quotaMap={quotaMap}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PolicyTypeTab — reusable component for any Organizations policy type
// ---------------------------------------------------------------------------
const PolicyTypeTab = ({ managementAccountId, policyTypeConfig, orgData, orgDataLoading, loadOrgData, onBackToPolicyTypes }) => {
  const isReadOnly = useReadOnlyMode();
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Policy type enablement check
  const [policyTypeEnabled, setPolicyTypeEnabled] = useState(null); // null=loading, true, false
  const [enabledCheckLoading, setEnabledCheckLoading] = useState(true);

  // Policy search
  const [policySearch, setPolicySearch] = useState('');
  const [debouncedPolicySearch, setDebouncedPolicySearch] = useState('');
  const policySearchTimeoutRef = useRef(null);

  const handlePolicySearchChange = (e) => {
    const value = e.target.value;
    setPolicySearch(value);
    if (policySearchTimeoutRef.current) clearTimeout(policySearchTimeoutRef.current);
    policySearchTimeoutRef.current = setTimeout(() => setDebouncedPolicySearch(value), 300);
  };

  // Policy detail view
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyDetail, setPolicyDetail] = useState(null);
  const [policyDetailLoading, setPolicyDetailLoading] = useState(false);
  const [policyTargets, setPolicyTargets] = useState([]);
  const [policyTargetsLoading, setPolicyTargetsLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);
  const [attachFromDetailOpen, setAttachFromDetailOpen] = useState(false);
  const [attachFromDetailTargetIds, setAttachFromDetailTargetIds] = useState([]);
  const [attachProgress, setAttachProgress] = useState(null);

  // Edit policy state
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editContent, setEditContent] = useState('');

  // FullAWSAccess detach warning
  const [fullAccessWarningOpen, setFullAccessWarningOpen] = useState(false);
  const [pendingDetachTargetId, setPendingDetachTargetId] = useState(null);
  const [selectedDetachTargets, setSelectedDetachTargets] = useState([]);

  // Create dialog
  const [createPolicyOpen, setCreatePolicyOpen] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyDescription, setNewPolicyDescription] = useState('');
  const [newPolicyContent, setNewPolicyContent] = useState('');

  // Validation (SCP-only)
  const [policyValidation, setPolicyValidation] = useState({ isValidating: false, result: null, error: null });

  // Quota tracking for attach dialog
  const [targetQuotas, setTargetQuotas] = useState({});
  const [quotasLoading, setQuotasLoading] = useState(false);

  // Tags
  const [policyTags, setPolicyTags] = useState([]);
  const [policyTagsLoading, setPolicyTagsLoading] = useState(false);
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [editingTagKey, setEditingTagKey] = useState(null); // non-null = editing mode
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');

  // Delete dialog
  const [deletePolicyOpen, setDeletePolicyOpen] = useState(false);
  const [deletePolicyTarget, setDeletePolicyTarget] = useState(null);

  // Hidden CK-managed policies and tenant-tag filtering are now enforced
  // server-side in prism-ops; the client trusts the response shape.
  const fetchPolicies = useCallback(async () => {
    if (!managementAccountId?.trim()) return;
    setPoliciesLoading(true);
    setError('');
    try {
      const data = await apiService.listOrgPolicies(managementAccountId.trim(), policyTypeConfig.key);
      setPolicies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch policies');
    } finally {
      setPoliciesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementAccountId, policyTypeConfig.key]);

  // Check if this policy type is enabled in the organization
  useEffect(() => {
    if (!managementAccountId?.trim()) return;
    let cancelled = false;
    setEnabledCheckLoading(true);
    (async () => {
      try {
        const enabledTypes = await apiService.getEnabledPolicyTypes(managementAccountId.trim());
        if (cancelled) return;
        const isEnabled = Array.isArray(enabledTypes) && enabledTypes.some(
          (t) => t.type === policyTypeConfig.key && t.status === 'ENABLED'
        );
        setPolicyTypeEnabled(isEnabled);
      } catch {
        if (!cancelled) setPolicyTypeEnabled(null);
      } finally {
        if (!cancelled) setEnabledCheckLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [managementAccountId, policyTypeConfig.key]);

  // Auto-fetch on mount (only if policy type is enabled)
  useEffect(() => {
    if (managementAccountId?.trim() && policyTypeEnabled === true) {
      fetchPolicies();
    }
  }, [fetchPolicies, managementAccountId, policyTypeEnabled]);

  // Create Policy
  const handleCreatePolicyOpen = () => {
    setNewPolicyName('');
    setNewPolicyDescription('');
    setNewPolicyContent(policyTypeConfig.defaultContent);
    setPolicyValidation({ isValidating: false, result: null, error: null });
    setCreatePolicyOpen(true);
  };

  const validatePolicy = async (policyContent) => {
    if (!policyContent || policyContent.trim() === '') {
      setPolicyValidation({ isValidating: false, result: null, error: null });
      return;
    }
    try {
      JSON.parse(policyContent);
    } catch (e) {
      setPolicyValidation({ isValidating: false, result: null, error: 'Invalid JSON format. Please check the syntax.' });
      return;
    }
    setPolicyValidation({ isValidating: true, result: null, error: null });
    try {
      const response = await apiService.validateIAMPolicy(policyContent, policyTypeConfig.validationPolicyType || 'IDENTITY_POLICY');
      if (response.success) {
        setPolicyValidation({ isValidating: false, result: response.data, error: null });
      } else {
        setPolicyValidation({ isValidating: false, result: null, error: response.message || 'Failed to validate policy' });
      }
    } catch (err) {
      setPolicyValidation({ isValidating: false, result: null, error: err.message || 'An error occurred during policy validation' });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidatePolicy = useCallback(debounce((content) => validatePolicy(content), 800), []);

  useEffect(() => {
    if (policyTypeConfig.hasValidation && createPolicyOpen && newPolicyContent) {
      debouncedValidatePolicy(newPolicyContent);
    }
    return () => debouncedValidatePolicy.cancel();
  }, [newPolicyContent, createPolicyOpen, debouncedValidatePolicy, policyTypeConfig.hasValidation]);

  const renderPolicyValidation = () => {
    if (!policyTypeConfig.hasValidation) return null;

    if (policyValidation.isValidating) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#F39C12' }}>
          <CircularProgress size={14} sx={{ color: '#F39C12' }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Validating policy...
          </Typography>
        </Box>
      );
    }

    if (policyValidation.error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#E74C3C' }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            {policyValidation.error}
          </Typography>
        </Box>
      );
    }

    if (!policyValidation.result) return null;

    const findings = policyValidation.result.findings || [];

    if (findings.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#27AE60' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy is valid
          </Typography>
        </Box>
      );
    }

    const errors = findings.filter((f) => f.findingType === 'ERROR');
    const warnings = findings.filter((f) => f.findingType === 'WARNING');
    const suggestions = findings.filter((f) => f.findingType === 'SUGGESTION');

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#F39C12' }}>
          <WarningIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy validation found issues
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FDF2F2', borderRadius: '6px', border: '1px solid #E74C3C20' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#E74C3C', mb: 0.5 }}>
              Errors ({errors.length})
            </Typography>
            {errors.map((err, i) => (
              <Typography key={`error-${i}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666', fontWeight: 500 }}>
                • <strong>{err.issueCode || 'Error'}:</strong> {err.findingDetails || 'Invalid policy'}
              </Typography>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FEF9E7', borderRadius: '6px', border: '1px solid #F39C1220' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#F39C12', mb: 0.5 }}>
              Warnings ({warnings.length})
            </Typography>
            {warnings.map((w, i) => (
              <Typography key={`warning-${i}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666', fontWeight: 500 }}>
                • <strong>{w.issueCode || 'Warning'}:</strong> {w.findingDetails || 'Policy warning'}
              </Typography>
            ))}
          </Box>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ p: 1.5, backgroundColor: '#EBF5FB', borderRadius: '6px', border: '1px solid #4698D320' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#4698D3', mb: 0.5 }}>
              Suggestions ({suggestions.length})
            </Typography>
            {suggestions.map((s, i) => (
              <Typography key={`suggestion-${i}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666', fontWeight: 500 }}>
                • <strong>{s.issueCode || 'Suggestion'}:</strong> {s.findingDetails || 'Policy suggestion'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const handleCreatePolicy = async () => {
    if (!newPolicyName.trim() || !newPolicyContent.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      JSON.parse(newPolicyContent);
      await apiService.createOrgPolicy(managementAccountId.trim(), newPolicyName.trim(), newPolicyDescription.trim(), newPolicyContent.trim(), policyTypeConfig.key);
      setCreatePolicyOpen(false);
      toast.success('Policy created successfully');
      await fetchPolicies();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Invalid JSON in policy content');
      } else {
        toast.error(err.message || 'Failed to create policy');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Policy
  const handleDeletePolicyOpen = (policy) => {
    setDeletePolicyTarget(policy);
    setDeletePolicyOpen(true);
  };

  const handleDeletePolicy = async () => {
    if (!deletePolicyTarget) return;
    setActionLoading(true);
    setError('');
    try {
      await apiService.deleteOrgPolicy(managementAccountId.trim(), deletePolicyTarget.id);
      setDeletePolicyOpen(false);
      toast.success('Policy deleted successfully');
      if (selectedPolicy && selectedPolicy.id === deletePolicyTarget.id) {
        setSelectedPolicy(null);
        setPolicyDetail(null);
        setPolicyTargets([]);
      }
      await fetchPolicies();
    } catch (err) {
      toast.error(err.message || 'Failed to delete policy');
    } finally {
      setActionLoading(false);
    }
  };

  // Policy detail view
  const handlePolicyClick = async (policy) => {
    setSelectedPolicy(policy);
    setDetailTab(0);
    setPolicyDetail(null);
    setPolicyTargets([]);
    setPolicyDetailLoading(true);
    try {
      const detail = await apiService.describeOrgPolicy(managementAccountId.trim(), policy.id);
      setPolicyDetail(detail);
    } catch (err) {
      setError(err.message || 'Failed to fetch policy details');
    } finally {
      setPolicyDetailLoading(false);
    }
  };

  // Fetch quotas once the org tree is available and the Attach dialog is open.
  // The tree loads lazily via loadOrgData(), so we can't run quotas eagerly
  // from the click handler — wait for both.
  useEffect(() => {
    if (attachFromDetailOpen && orgData?.tree) {
      fetchTargetQuotas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachFromDetailOpen, orgData]);

  const fetchTargetQuotas = async () => {
    if (!orgData?.tree) return;
    setQuotasLoading(true);
    const allNodes = [];
    const collect = (n) => { allNodes.push(n); if (n.children) n.children.forEach(collect); };
    orgData.tree.forEach(collect);

    const quotas = {};
    for (let i = 0; i < allNodes.length; i += 5) {
      const batch = allNodes.slice(i, i + 5);
      const results = await Promise.all(
        batch.map(async (n) => {
          try {
            // Use the count-only endpoint so the displayed N/5 matches AWS's
            // real per-target limit (which counts ALL attachments, not just
            // the tenant-owned ones that listOrgPoliciesForTarget returns).
            const resp = await apiService.countOrgPoliciesForTarget(managementAccountId.trim(), n.id, policyTypeConfig.key);
            const count = (resp && typeof resp.count === 'number') ? resp.count : 0;
            return { id: n.id, count };
          } catch {
            return { id: n.id, count: -1 };
          }
        })
      );
      results.forEach((r) => { quotas[r.id] = r.count; });
    }
    setTargetQuotas(quotas);
    setQuotasLoading(false);
  };

  const fetchPolicyTargets = async (policyId) => {
    setPolicyTargetsLoading(true);
    try {
      const data = await apiService.listTargetsForOrgPolicy(managementAccountId.trim(), policyId);
      setPolicyTargets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch policy targets');
    } finally {
      setPolicyTargetsLoading(false);
    }
  };

  const fetchPolicyTags = async (policyId) => {
    setPolicyTagsLoading(true);
    try {
      const data = await apiService.listTagsForPolicy(managementAccountId.trim(), policyId);
      // Hide the ck:tenant ownership tag — it's managed by Prism and removing
      // it would orphan the policy from its tenant. The backend also rejects
      // mutations on it; this just keeps it out of the UI.
      const visible = (Array.isArray(data) ? data : []).filter((t) => t.key !== 'ck:tenant');
      setPolicyTags(visible);
    } catch (err) {
      setError(err.message || 'Failed to fetch policy tags');
    } finally {
      setPolicyTagsLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!selectedPolicy || !newTagKey.trim()) return;
    setActionLoading(true);
    try {
      // If editing and key changed, delete the old key first
      if (editingTagKey && editingTagKey !== newTagKey.trim()) {
        await apiService.untagPolicy(managementAccountId.trim(), selectedPolicy.id, [editingTagKey]);
      }
      await apiService.tagPolicy(managementAccountId.trim(), selectedPolicy.id, [{ key: newTagKey.trim(), value: newTagValue.trim() }]);
      toast.success(editingTagKey ? 'Tag updated' : 'Tag added');
      setAddTagOpen(false);
      setEditingTagKey(null);
      setNewTagKey('');
      setNewTagValue('');
      await fetchPolicyTags(selectedPolicy.id);
    } catch (err) {
      toast.error(err.message || 'Failed to save tag');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveTag = async (tagKey) => {
    if (!selectedPolicy) return;
    setActionLoading(true);
    try {
      await apiService.untagPolicy(managementAccountId.trim(), selectedPolicy.id, [tagKey]);
      toast.success('Tag removed');
      await fetchPolicyTags(selectedPolicy.id);
    } catch (err) {
      toast.error(err.message || 'Failed to remove tag');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetailTabChange = (_, newValue) => {
    setDetailTab(newValue);
    if (newValue === 1 && selectedPolicy && policyTargets.length === 0) {
      fetchPolicyTargets(selectedPolicy.id);
    }
    if (newValue === 2 && selectedPolicy && policyTags.length === 0) {
      fetchPolicyTags(selectedPolicy.id);
    }
  };

  const handleDetachFromDetail = async (targetId) => {
    if (!selectedPolicy) return;
    if (policyTypeConfig.hasFullAWSAccessWarning && selectedPolicy.name === 'FullAWSAccess') {
      setPendingDetachTargetId(targetId);
      setFullAccessWarningOpen(true);
      return;
    }
    await executeDetachFromDetail(targetId);
  };

  const handleBatchDetach = async () => {
    if (!selectedPolicy || selectedDetachTargets.length === 0) return;
    setActionLoading(true);
    try {
      const results = await Promise.allSettled(
        selectedDetachTargets.map((targetId) =>
          apiService.detachOrgPolicy(managementAccountId.trim(), selectedPolicy.id, targetId)
        )
      );
      const failures = results
        .map((r, i) => ({ r, targetId: selectedDetachTargets[i] }))
        .filter(({ r }) => r.status === 'rejected');
      const successCount = results.length - failures.length;
      failures.forEach(({ r, targetId }) =>
        console.error('detach failed for', targetId, r.reason)
      );
      setSelectedDetachTargets([]);
      if (failures.length === 0) {
        toast.success(`Detached from ${successCount} target(s)`);
      } else {
        const firstErr = failures[0].r.reason?.message || 'unknown error';
        toast.warning(`Detached ${successCount}, failed ${failures.length} (first error: ${firstErr})`);
      }
      await fetchPolicyTargets(selectedPolicy.id);
    } catch (err) {
      toast.error(err.message || 'Batch detach failed');
    } finally {
      setActionLoading(false);
    }
  };

  const executeDetachFromDetail = async (targetId) => {
    setActionLoading(true);
    setError('');
    try {
      await apiService.detachOrgPolicy(managementAccountId.trim(), selectedPolicy.id, targetId);
      toast.success('Policy detached from target');
      setSelectedDetachTargets((prev) => prev.filter((id) => id !== targetId));
      await fetchPolicyTargets(selectedPolicy.id);
    } catch (err) {
      toast.error(err.message || 'Failed to detach policy');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAttachFromDetail = async () => {
    if (!selectedPolicy || attachFromDetailTargetIds.length === 0) return;
    const targets = [...attachFromDetailTargetIds];
    const total = targets.length;
    const results = [];
    setAttachProgress({ current: 0, total, results: [] });
    setActionLoading(true);
    setError('');

    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      try {
        await apiService.attachOrgPolicy(managementAccountId.trim(), selectedPolicy.id, target.id);
        results.push({ targetId: target.id, targetName: target.name, success: true });
      } catch (err) {
        results.push({ targetId: target.id, targetName: target.name, success: false, error: err.message || 'Failed to attach' });
      }
      setAttachProgress({ current: i + 1, total, results: [...results] });
    }

    setActionLoading(false);
    const failures = results.filter((r) => !r.success);
    if (failures.length === 0) {
      setAttachFromDetailOpen(false);
      setAttachProgress(null);
      setAttachFromDetailTargetIds([]);
      toast.success(`Policy attached to ${total} target(s)`);
      await fetchPolicyTargets(selectedPolicy.id);
    } else {
      toast.warning(`Attached to ${total - failures.length} of ${total} targets`);
      await fetchPolicyTargets(selectedPolicy.id);
    }
  };

  const handleEditPolicyOpen = () => {
    if (!selectedPolicy || !policyDetail) return;
    setEditName(selectedPolicy.name);
    setEditDescription(selectedPolicy.description || '');
    try {
      setEditContent(JSON.stringify(JSON.parse(policyDetail.content), null, 2));
    } catch {
      setEditContent(policyDetail.content);
    }
    setEditMode(true);
  };

  const handleEditPolicyCancel = () => {
    setEditMode(false);
  };

  const handleEditPolicySave = async () => {
    if (!selectedPolicy) return;
    setActionLoading(true);
    try {
      await apiService.updateOrgPolicy(
        managementAccountId.trim(),
        selectedPolicy.id,
        editName.trim() !== selectedPolicy.name ? editName.trim() : '',
        editDescription.trim() !== (selectedPolicy.description || '') ? editDescription.trim() : '',
        editContent.trim(),
      );
      toast.success('Policy updated successfully');
      setEditMode(false);
      // Refresh detail
      const detail = await apiService.describeOrgPolicy(managementAccountId.trim(), selectedPolicy.id);
      setPolicyDetail(detail);
      // Update selectedPolicy with new name/description
      setSelectedPolicy((prev) => ({
        ...prev,
        name: editName.trim() || prev.name,
        description: editDescription.trim(),
      }));
      await fetchPolicies();
    } catch (err) {
      toast.error(err.message || 'Failed to update policy');
    } finally {
      setActionLoading(false);
    }
  };

  const createButtonDisabled = policyTypeConfig.hasValidation
    ? actionLoading || !newPolicyName.trim() || !newPolicyContent.trim() || policyValidation.isValidating || !!policyValidation.error || (policyValidation.result?.findings || []).some((f) => f.findingType === 'ERROR')
    : actionLoading || !newPolicyName.trim() || !newPolicyContent.trim();

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {selectedPolicy === null ? (
        /* ---- List View ---- */
        <>
          {onBackToPolicyTypes && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                onClick={onBackToPolicyTypes}
                sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: accentColor, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Policies
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.875rem' }}>&gt;</Typography>
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66', fontWeight: 500 }}>
                {policyTypeConfig.label}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
                {policyTypeConfig.label}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem' }}>
                {policyTypeConfig.description}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchPolicies} disabled={policiesLoading || policyTypeEnabled === false} sx={{ color: accentColor }}>
                  <RefreshIcon sx={{
                    animation: policiesLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                  }} />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                onClick={handleCreatePolicyOpen}
                disabled={isReadOnly || policyTypeEnabled === false}
                startIcon={<AddIcon />}
                sx={{
                  textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                  bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' },
                }}
              >
                Create Policy
              </Button>
            </Box>
          </Box>

          {enabledCheckLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#4698D3' }} />
            </Box>
          ) : policyTypeEnabled === false ? (
            <Alert severity="info" sx={{ borderRadius: '12px', fontFamily: 'Metropolis, sans-serif' }}>
              This policy type is not enabled in your organization. Enable it in the AWS Organizations console to use this feature.
            </Alert>
          ) : policiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#4698D3' }} />
            </Box>
          ) : policies.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
              <Avatar sx={{ width: 56, height: 56, backgroundColor: 'rgba(70, 152, 211, 0.08)' }}>
                <policyTypeConfig.icon sx={{ fontSize: 28, color: accentColor }} />
              </Avatar>
              <Typography sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '1rem' }}>
                No {policyTypeConfig.label.toLowerCase()} found
              </Typography>
              <Typography sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', textAlign: 'center', maxWidth: 320 }}>
                Create a policy to get started.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder={`Search ${policyTypeConfig.label.toLowerCase()}...`}
                  value={policySearch}
                  onChange={handlePolicySearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999999' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300, ...inputStyle }}
                />
              </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Name</TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>ID</TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Description</TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.filter((p) =>
                    p.name.toLowerCase().includes(debouncedPolicySearch.toLowerCase()) ||
                    (p.description || '').toLowerCase().includes(debouncedPolicySearch.toLowerCase()) ||
                    p.id.toLowerCase().includes(debouncedPolicySearch.toLowerCase())
                  ).map((policy) => (
                    <TableRow key={policy.id} hover sx={{ cursor: 'pointer' }} onClick={() => handlePolicyClick(policy)}>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PolicyIcon sx={{ fontSize: '1rem', color: '#4698D3' }} />
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem',
                              color: '#4698D3', fontWeight: 500,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {policy.name}
                          </Typography>
                          {policy.aws_managed && (
                            <Chip label="AWS Managed" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'rgba(155, 89, 182, 0.1)', color: '#9B59B6' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#999' }}>{policy.id}</TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666', maxWidth: 300 }}>
                        {policy.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={policy.type} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'rgba(70, 152, 211, 0.1)', color: '#4698D3' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </>
      ) : (
        /* ---- Detail View ---- */
        <>
          {/* Breadcrumb navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
            {onBackToPolicyTypes && (
              <>
                <Typography
                  onClick={onBackToPolicyTypes}
                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: accentColor, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  Policies
                </Typography>
                <Typography sx={{ color: '#999', fontSize: '0.875rem' }}>&gt;</Typography>
              </>
            )}
            <Typography
              onClick={() => { setSelectedPolicy(null); setPolicyDetail(null); setPolicyTargets([]); setPolicyTags([]); setEditMode(false); }}
              sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: accentColor, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {policyTypeConfig.label}
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.875rem' }}>&gt;</Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66', fontWeight: 500 }}>
              {selectedPolicy?.name}
            </Typography>
          </Box>

          {/* Policy Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Policy Name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    size="small"
                    sx={{ maxWidth: 400, ...inputStyle }}
                  />
                  <TextField
                    label="Description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    size="small"
                    sx={{ maxWidth: 500, ...inputStyle }}
                  />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PolicyIcon sx={{ color: '#4698D3' }} />
                    <Typography variant="h6" sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
                      {selectedPolicy.name}
                    </Typography>
                    {selectedPolicy.aws_managed && (
                      <Chip label="AWS Managed" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'rgba(155, 89, 182, 0.1)', color: '#9B59B6' }} />
                    )}
                  </Box>
                  {selectedPolicy.description && (
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Metropolis, sans-serif', ml: 4 }}>
                      {selectedPolicy.description}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#999', fontFamily: 'monospace', fontSize: '0.75rem', ml: 4, mt: 0.5 }}>
                    {selectedPolicy.id}
                  </Typography>
                </>
              )}
            </Box>
            {!selectedPolicy.aws_managed && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editMode ? (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={handleEditPolicyCancel}
                      disabled={actionLoading}
                      sx={{
                        textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                        borderColor: '#E5E5E5', color: '#666',
                        '&:hover': { borderColor: '#D9DBDC', bgcolor: '#F9FBFD' },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleEditPolicySave}
                      disabled={isReadOnly || actionLoading || !editName.trim() || !editContent.trim()}
                      sx={{
                        textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                        bgcolor: accentColor, '&:hover': { bgcolor: '#3580B8' },
                      }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleEditPolicyOpen}
                      disabled={isReadOnly || !policyDetail}
                      sx={{
                        textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                        borderColor: accentColor, color: accentColor,
                        '&:hover': { borderColor: '#3580B8', bgcolor: 'rgba(70, 152, 211, 0.05)' },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteIcon />}
                      disabled={isReadOnly}
                      onClick={() => handleDeletePolicyOpen(selectedPolicy)}
                      sx={{
                        textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                        borderColor: '#E74C3C', color: '#E74C3C',
                        '&:hover': { borderColor: '#C0392B', bgcolor: 'rgba(231, 76, 60, 0.05)' },
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Sub-tabs */}
          <Tabs
            value={detailTab}
            onChange={handleDetailTabChange}
            sx={{
              mb: 2,
              '& .MuiTab-root': { textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, minHeight: 40 },
              '& .Mui-selected': { color: '#4698D3' },
              '& .MuiTabs-indicator': { backgroundColor: '#4698D3' },
            }}
          >
            <Tab label="Policy content" />
            <Tab label="Targets" />
            <Tab label="Tags" />
          </Tabs>

          {/* Content tab */}
          {detailTab === 0 && (
            policyDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: '#4698D3' }} />
              </Box>
            ) : editMode ? (
              <TextField
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                fullWidth
                multiline
                rows={18}
                sx={{
                  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' },
                  ...inputStyle,
                }}
              />
            ) : policyDetail?.content ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2, bgcolor: '#F8F9FA', borderRadius: '8px',
                  maxHeight: 500, overflow: 'auto', position: 'relative',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const content = (() => { try { return JSON.stringify(JSON.parse(policyDetail.content), null, 2); } catch { return policyDetail.content; } })();
                        navigator.clipboard.writeText(content);
                        toast.success('Policy JSON copied to clipboard');
                      }}
                      sx={{ color: accentColor }}
                    >
                      <CopyIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {(() => {
                    try { return JSON.stringify(JSON.parse(policyDetail.content), null, 2); }
                    catch { return policyDetail.content; }
                  })()}
                </pre>
              </Paper>
            ) : (
              <Typography sx={{ py: 4, textAlign: 'center', color: '#999', fontFamily: 'Metropolis, sans-serif' }}>
                No content available.
              </Typography>
            )
          )}

          {/* Targets tab */}
          {detailTab === 1 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                {selectedDetachTargets.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DetachIcon />}
                    onClick={handleBatchDetach}
                    disabled={isReadOnly || actionLoading}
                    sx={{
                      textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                      borderColor: '#F39C12', color: '#F39C12',
                      '&:hover': { borderColor: '#E67E22', bgcolor: 'rgba(243, 156, 18, 0.05)' },
                    }}
                  >
                    Detach selected ({selectedDetachTargets.length})
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AttachIcon />}
                  disabled={isReadOnly}
                  onClick={() => { setAttachFromDetailTargetIds([]); setAttachProgress(null); setAttachFromDetailOpen(true); loadOrgData(); }}
                  sx={{
                    textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                    bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' },
                  }}
                >
                  Attach
                </Button>
              </Box>

              {policyTargetsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#4698D3' }} />
                </Box>
              ) : policyTargets.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 5 }}>
                  <Avatar sx={{ width: 48, height: 48, backgroundColor: 'rgba(70, 152, 211, 0.08)' }}>
                    <AttachIcon sx={{ fontSize: 24, color: accentColor }} />
                  </Avatar>
                  <Typography sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                    No targets attached
                  </Typography>
                  <Typography sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                    Use the Attach button to attach this policy to targets.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={policyTargets.length > 0 && selectedDetachTargets.length === policyTargets.length}
                            indeterminate={selectedDetachTargets.length > 0 && selectedDetachTargets.length < policyTargets.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDetachTargets(policyTargets.map((t) => t.target_id));
                              } else {
                                setSelectedDetachTargets([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Name</TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Type</TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>ID</TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {policyTargets.map((target) => (
                        <TableRow key={target.target_id} hover selected={selectedDetachTargets.includes(target.target_id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedDetachTargets.includes(target.target_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDetachTargets((prev) => [...prev, target.target_id]);
                                } else {
                                  setSelectedDetachTargets((prev) => prev.filter((id) => id !== target.target_id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                            {target.name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={target.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : target.type}
                              size="small"
                              sx={{
                                fontSize: '0.65rem', height: 20, fontWeight: 600,
                                bgcolor: target.type === 'ACCOUNT' ? 'rgba(39, 174, 96, 0.1)' : target.type === 'ORGANIZATIONAL_UNIT' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(70, 152, 211, 0.1)',
                                color: target.type === 'ACCOUNT' ? '#27AE60' : target.type === 'ORGANIZATIONAL_UNIT' ? '#F39C12' : '#4698D3',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#999' }}>
                            {target.target_id}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Detach from target"}>
                              <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDetachFromDetail(target.target_id)}
                                disabled={isReadOnly || actionLoading}
                                sx={{ color: '#F39C12', '&:hover': { bgcolor: 'rgba(243, 156, 18, 0.1)' } }}
                              >
                                <DetachIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tags tab */}
          {detailTab === 2 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingTagKey(null); setNewTagKey(''); setNewTagValue(''); setAddTagOpen(true); }}
                  disabled={isReadOnly || selectedPolicy?.aws_managed}
                  sx={{
                    textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600,
                    bgcolor: accentColor, '&:hover': { bgcolor: '#3580B8' },
                  }}
                >
                  Add Tag
                </Button>
              </Box>

              {policyTagsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: accentColor }} />
                </Box>
              ) : policyTags.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 5 }}>
                  <Typography sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                    No tags
                  </Typography>
                  <Typography sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                    Use the Add Tag button to tag this policy.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Key</TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>Value</TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {policyTags.map((tag) => (
                        <TableRow key={tag.key} hover>
                          <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', fontWeight: 500 }}>
                            {tag.key}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666' }}>
                            {tag.value || <Typography component="span" sx={{ color: '#ccc', fontStyle: 'italic', fontSize: '0.8rem' }}>empty</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Edit tag"}>
                              <span>
                              <IconButton
                                size="small"
                                onClick={() => { setEditingTagKey(tag.key); setNewTagKey(tag.key); setNewTagValue(tag.value); setAddTagOpen(true); }}
                                disabled={isReadOnly || actionLoading || selectedPolicy?.aws_managed}
                                sx={{ color: accentColor, '&:hover': { bgcolor: 'rgba(70, 152, 211, 0.1)' } }}
                              >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Remove tag"}>
                              <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveTag(tag.key)}
                                disabled={isReadOnly || actionLoading || selectedPolicy?.aws_managed}
                                sx={{ color: '#E74C3C', '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.1)' } }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Add Tag Dialog */}
              <Dialog open={addTagOpen} onClose={() => setAddTagOpen(false)} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
                <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600 }}>
                  {editingTagKey ? 'Edit Tag' : 'Add Tag'}
                </DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus label="Key" value={newTagKey}
                    onChange={(e) => setNewTagKey(e.target.value)} fullWidth size="small" sx={{ mb: 2, mt: 1 }}
                  />
                  <TextField
                    autoFocus={!!editingTagKey} label="Value (optional)" value={newTagValue}
                    onChange={(e) => setNewTagValue(e.target.value)} fullWidth size="small"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setAddTagOpen(false)} disabled={actionLoading}>Cancel</Button>
                  <Button
                    onClick={handleAddTag} variant="contained"
                    disabled={isReadOnly || actionLoading || !newTagKey.trim()}
                    sx={{ bgcolor: accentColor, '&:hover': { bgcolor: '#3580B8' } }}
                  >
                    {actionLoading ? <CircularProgress size={20} color="inherit" /> : editingTagKey ? 'Save' : 'Add'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </>
      )}

      {/* Create Policy Dialog */}
      <Dialog open={createPolicyOpen} onClose={() => setCreatePolicyOpen(false)} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <policyTypeConfig.icon sx={{ color: '#4698D3' }} />
            Create {policyTypeConfig.label.replace(/ies$/, 'y').replace(/s$/, '')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Policy Name"
            value={newPolicyName}
            onChange={(e) => setNewPolicyName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Description (optional)"
            value={newPolicyDescription}
            onChange={(e) => setNewPolicyDescription(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Policy JSON"
            value={newPolicyContent}
            onChange={(e) => setNewPolicyContent(e.target.value)}
            fullWidth
            multiline
            rows={12}
            sx={{
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' },
            }}
          />
          {renderPolicyValidation()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePolicyOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            onClick={handleCreatePolicy}
            variant="contained"
            disabled={isReadOnly || createButtonDisabled}
            sx={{ bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' } }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Policy Dialog */}
      <Dialog open={deletePolicyOpen} onClose={() => setDeletePolicyOpen(false)} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#E74C3C' }}>
          Delete Policy
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deletePolicyTarget?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#999', fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {deletePolicyTarget?.id}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#999' }}>
            The policy must be detached from all targets first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePolicyOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            onClick={handleDeletePolicy}
            variant="contained"
            disabled={isReadOnly || actionLoading}
            sx={{ bgcolor: '#E74C3C', '&:hover': { bgcolor: '#C0392B' } }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attach Policy from Detail View Dialog */}
      <Dialog open={attachFromDetailOpen} onClose={() => { if (!actionLoading) { setAttachFromDetailOpen(false); setAttachProgress(null); setAttachFromDetailTargetIds([]); } }} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachIcon sx={{ color: '#4698D3' }} />
            Attach {selectedPolicy?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
            Select one or more targets to attach this policy to:
          </Typography>
          {quotasLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CircularProgress size={14} sx={{ color: accentColor }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#999', fontFamily: 'Metropolis, sans-serif' }}>Loading quotas...</Typography>
            </Box>
          )}
          {orgData?.tree ? (
            <Box sx={{ maxHeight: 350, overflowY: 'auto', border: '1px solid #E5E5E5', borderRadius: '8px', p: 1 }}>
              {orgData.tree.map((rootNode) => (
                <OrgTreeCheckbox
                  key={rootNode.id}
                  node={{ ...rootNode, name: 'Root' }}
                  depth={0}
                  selectedIds={attachFromDetailTargetIds}
                  onToggle={(node) => {
                    setAttachFromDetailTargetIds((prev) =>
                      prev.some((n) => n.id === node.id)
                        ? prev.filter((n) => n.id !== node.id)
                        : [...prev, node]
                    );
                  }}
                  excludeIds={policyTargets.map((t) => t.target_id)}
                  hideRoot={policyTypeConfig.key === 'SERVICE_CONTROL_POLICY'}
                  quotaMap={Object.keys(targetQuotas).length > 0 ? targetQuotas : null}
                />
              ))}
            </Box>
          ) : orgDataLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 1 }}>
              <CircularProgress size={18} sx={{ color: accentColor }} />
              <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Metropolis, sans-serif' }}>Loading organization tree…</Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999' }}>Organization tree not available.</Typography>
          )}
          {attachFromDetailTargetIds.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {attachFromDetailTargetIds.map((t) => (
                <Chip
                  key={t.id}
                  label={`${t.name} (${t.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : t.type})`}
                  size="small"
                  onDelete={() => setAttachFromDetailTargetIds((prev) => prev.filter((n) => n.id !== t.id))}
                  sx={{
                    backgroundColor: t.type === 'ACCOUNT' ? 'rgba(39, 174, 96, 0.1)' : t.type === 'OU' || t.type === 'ORGANIZATIONAL_UNIT' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(70, 152, 211, 0.1)',
                    color: t.type === 'ACCOUNT' ? '#27AE60' : t.type === 'OU' || t.type === 'ORGANIZATIONAL_UNIT' ? '#F39C12' : '#4698D3',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              ))}
            </Box>
          )}
          {attachProgress && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#666' }}>
                  Attaching to {attachProgress.current}/{attachProgress.total} targets...
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#999' }}>
                  {Math.round((attachProgress.current / attachProgress.total) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(attachProgress.current / attachProgress.total) * 100}
                sx={{
                  height: 6, borderRadius: 3,
                  bgcolor: 'rgba(70, 152, 211, 0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#4698D3', borderRadius: 3 },
                }}
              />
              {attachProgress.current === attachProgress.total && (
                <Box sx={{ mt: 1.5 }}>
                  {attachProgress.results.filter((r) => r.success).length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: '#27AE60' }}>
                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                        Successfully attached to {attachProgress.results.filter((r) => r.success).length} target(s)
                      </Typography>
                    </Box>
                  )}
                  {attachProgress.results.filter((r) => !r.success).map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: '#E74C3C' }}>
                      <ErrorIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                        {r.targetName}: {r.error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setAttachFromDetailOpen(false); setAttachProgress(null); setAttachFromDetailTargetIds([]); }}
            disabled={actionLoading && !(attachProgress && attachProgress.current === attachProgress.total)}
          >
            {attachProgress && attachProgress.current === attachProgress.total ? 'Close' : 'Cancel'}
          </Button>
          {!(attachProgress && attachProgress.current === attachProgress.total) && (
            <Button
              onClick={handleAttachFromDetail}
              variant="contained"
              disabled={isReadOnly || actionLoading || attachFromDetailTargetIds.length === 0}
              sx={{ bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' } }}
            >
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : `Attach${attachFromDetailTargetIds.length > 0 ? ` (${attachFromDetailTargetIds.length})` : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* FullAWSAccess Detach Warning Dialog */}
      <Dialog
        open={fullAccessWarningOpen}
        onClose={() => { setFullAccessWarningOpen(false); setPendingDetachTargetId(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#E74C3C' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#E74C3C' }} />
            Detach FullAWSAccess
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#333' }}>
            Detaching <strong>FullAWSAccess</strong> will disable <strong>ALL</strong> operations on the target.
            No API calls, console actions, or service operations will be permitted.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Metropolis, sans-serif', color: '#999' }}>
            Only proceed if you are absolutely sure.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFullAccessWarningOpen(false); setPendingDetachTargetId(null); }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setFullAccessWarningOpen(false);
              executeDetachFromDetail(pendingDetachTargetId);
              setPendingDetachTargetId(null);
            }}
            variant="contained"
            disabled={isReadOnly}
            sx={{ bgcolor: '#E74C3C', '&:hover': { bgcolor: '#C0392B' } }}
          >
            Detach
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// OrgServices — main page component
// ---------------------------------------------------------------------------
const OrgServices = () => {
  const [managementAccountId, setManagementAccountId] = useState('');
  const [orgData, setOrgData] = useState(null);
  const [orgDataLoading, setOrgDataLoading] = useState(false);
  const orgDataLoadingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPolicyType, setSelectedPolicyType] = useState(null);
  const [enabledPolicyTypes, setEnabledPolicyTypes] = useState([]);
  const [enabledTypesLoading, setEnabledTypesLoading] = useState(false);

  // Auto-load management account ID. Org structure is fetched lazily — only
  // when the user opens the Attach dialog — since browsing/editing policies
  // doesn't need it.
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiService.getCurrentCustomer();
        const mgmtId = res?.data?.customer?.managementAccountId;
        if (mgmtId) {
          setManagementAccountId(mgmtId);
        } else {
          setError('Org Services management account has not been configured. Contact your application administrator.');
        }
      } catch (err) {
        setError(err.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  // loadOrgData fetches the org tree once per page session. Idempotent —
  // safe to call from multiple Attach-click sites.
  const loadOrgData = useCallback(async () => {
    if (!managementAccountId) return;
    if (orgData || orgDataLoadingRef.current) return;
    orgDataLoadingRef.current = true;
    setOrgDataLoading(true);
    try {
      const data = await apiService.getOrgStructure(managementAccountId);
      setOrgData(data);
    } catch (orgErr) {
      console.error('Failed to load org structure for attach picker:', orgErr);
    } finally {
      orgDataLoadingRef.current = false;
      setOrgDataLoading(false);
    }
  }, [managementAccountId, orgData]);

  // Fetch enabled policy types when management account is loaded
  useEffect(() => {
    if (!managementAccountId) return;
    const fetchEnabled = async () => {
      setEnabledTypesLoading(true);
      try {
        const data = await apiService.getEnabledPolicyTypes(managementAccountId.trim());
        setEnabledPolicyTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch enabled policy types:', err);
      } finally {
        setEnabledTypesLoading(false);
      }
    };
    fetchEnabled();
  }, [managementAccountId]);

  const isPolicyTypeEnabled = (policyKey) => {
    return enabledPolicyTypes.some((ep) => ep.type === policyKey && ep.status === 'ENABLED');
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ minHeight: '100vh', paddingTop: 32, paddingBottom: 32, paddingLeft: 32, paddingRight: 32, backgroundColor: '#F9FBFD' }}
    >
        {/* Header */}
        <motion.div variants={sectionVariants}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: '1px solid #E5E5E5',
              p: 3,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: accentColor, width: 48, height: 48 }}>
                  <AccountTreeIcon sx={{ fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Bitter, Georgia, serif',
                      fontWeight: 600,
                      fontSize: '1.75rem',
                      color: '#253E66',
                    }}
                  >
                    Policies
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.875rem',
                      color: '#999999',
                      fontWeight: 500,
                    }}
                  >
                    View and manage AWS Organizations policies
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Paper elevation={0} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E5E5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2, py: 6 }}>
            <CircularProgress size={40} sx={{ color: accentColor }} />
            <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.95rem' }}>
              Loading policies...
            </Typography>
          </Paper>
        )}

        {/* Policies */}
        {!loading && managementAccountId && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E5E5', borderRadius: '12px', overflow: 'hidden' }}>

            {/* Policies landing page */}
            {selectedPolicyType === null && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', mb: 2 }}>
                  Supported policy types
                </Typography>
                {enabledTypesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: accentColor }} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                          <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                            Policy type
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5', width: 120 }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {POLICY_TYPES.map((pt, index) => {
                          const enabled = isPolicyTypeEnabled(pt.key);
                          return (
                            <TableRow
                              key={pt.key}
                              hover
                              onClick={() => setSelectedPolicyType(index)}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(70, 152, 211, 0.04)' } }}
                            >
                              <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: accentColor }}>
                                  {pt.label}
                                </Typography>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666', mt: 0.25 }}>
                                  {pt.description}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                                <Chip
                                  label={enabled ? 'Enabled' : 'Disabled'}
                                  size="small"
                                  sx={{
                                    backgroundColor: enabled ? '#E8F5E9' : '#F5F5F5',
                                    color: enabled ? '#2E7D32' : '#999999',
                                    fontFamily: 'Metropolis, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Selected policy type drill-down */}
            {selectedPolicyType !== null && (
              <PolicyTypeTab
                managementAccountId={managementAccountId.trim()}
                policyTypeConfig={POLICY_TYPES[selectedPolicyType]}
                orgData={orgData}
                orgDataLoading={orgDataLoading}
                loadOrgData={loadOrgData}
                onBackToPolicyTypes={() => setSelectedPolicyType(null)}
              />
            )}
          </Paper>
        </motion.div>
        )}

    </motion.div>
  );
};
export default OrgServices;
